import { computed, inject, Injectable } from '@angular/core';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from '../../../shared/components/listing-table/listing-table.component';
import { OrderDirection } from '../../equipment/devices/components/devices.models';

import { SubscriptionManagementService } from './services/subscription-management.service';
import {
  SEARCH_TYPES,
  tableConfig,
  tableSortFiled,
  SUBSCRIPTION_SEARCH_MAX_WIDTH,
  SUBSCRIPTION_SEARCH_MAX_LENGTH
} from './subscription-management.constants';
import { SubscriptionManagement, SubscriptionManagementTable } from './subscription-management.types';

import { TableData } from '../../../shared/types/api-response.types';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { PermissionValue, ResourceValue } from '../../../services/permissions/permissions.types';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { toSignal } from '@angular/core/rxjs-interop';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { SubscriptionListingFiltersSourceService } from './subscription-listing-filters-adapter.service';
import { ExportCsvConfig } from 'src/app/services/file/export-data.service';
import { Store } from '@ngrx/store';
import { isSingleCommodityClient } from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable()
export class SubscriptionManagementListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly subscriptionManagementService = inject(SubscriptionManagementService);
  private readonly permissions = inject(PermissionsService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);

  readonly pageTitle = TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TITLE.SUBSCRIPTION_MANAGEMENT;
  readonly searchTypeList = computed(() => SEARCH_TYPES);
  readonly selectedSearchType = computed(() => SEARCH_TYPES[0]);
  readonly searchTextMaxLength = SUBSCRIPTION_SEARCH_MAX_LENGTH;
  readonly searchMaxWidth = SUBSCRIPTION_SEARCH_MAX_WIDTH;
  readonly sortField = tableSortFiled;
  readonly sorting = computed(() => ({
    columnName: tableSortFiled.subscriptionId,
    orderDirection: OrderDirection.DESC
  }));
  readonly hasRight = toSignal(this.permissions.hasRight(ResourceValue.SUBSCRIPTION_MANAGEMENT, PermissionValue.EDIT));
  readonly tableColumns = computed(() => {
    const config = this.hasRight() ? tableConfig : tableConfig.filter(col => col.field !== 'edit');
    return config.map(col => (col.field === 'commodity' ? { ...col, isHidden: this.#isSingleCommodityClient() } : col));
  });
  readonly dataSource: ListingDataSource<SubscriptionManagementTable> = {
    getAll: tableState => {
      return this.subscriptionManagementService.getTableData(tableState);
    },
    total: 0
  };
  readonly tableCmp = ListingTableComponent;

  readonly exportCsv = {
    fileName: 'Export-Subscriptions',
    mapData: (data: TableData<SubscriptionManagement>): ExportCsvConfig[] => {
      return data.data.map(item => {
        return {
          subscriptionId: { title: 'SubscriptionID', value: item.subscriptionId },
          subscriptionName: { title: 'SubscriptionName', value: item.subscriptionName },
          eventGroup: { title: 'EventGroup', value: item.eventGroup },
          eventName: { title: 'EventName', value: item.eventName },
          severityRules: { title: 'Severity', value: item.severityRules.join(', ') },
          commodity: { title: 'Commodity', value: item.commodity },
          reportGroup: { title: 'ReportGroup', value: item.reportGroup },
          channel: { title: 'Channel', value: item.channel },
          contact: { title: 'Contact', value: item.contact },
          delivery: { title: 'Delivery', value: item.delivery }
        };
      });
    }
  };
  readonly pageId = ListingPageId.SubscriptionManagement;
  readonly filterSource = inject(SubscriptionListingFiltersSourceService);
  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
